import React, { useState, ChangeEvent, FormEvent } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { format, addDays } from 'date-fns';
import { es } from 'date-fns/locale';

interface FormData {
  nombre: string;
  empresa: string;
  email: string;
  telefono: string;
  fecha: string;
  motivo: string;
  urgente: boolean;
}

export default function Visitanos() {
  const [formData, setFormData] = useState<FormData>({
    nombre: '',
    empresa: '',
    email: '',
    telefono: '',
    fecha: '',
    motivo: '',
    urgente: false,
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [sending, setSending] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  // Genera 3 fechas disponibles a partir de 35 días desde hoy
  const generateAvailableDates = () => {
    const startDate = addDays(new Date(), 35);
    return [0, 1, 2].map(i => {
      const date = addDays(startDate, i);
      return {
        value: format(date, 'yyyy-MM-dd'),
        label: format(date, "EEEE d 'de' MMMM", { locale: es })
      };
    });
  };

  const availableDates = generateAvailableDates();

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSending(true);

    // Simulamos una espera y éxito para la demo
    await new Promise(resolve => setTimeout(resolve, 1500));
    setFormSubmitted(true);
    setSending(false);
  };

  const nextStep = () => {
    setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  // Animaciones
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    initial: { opacity: 0 },
    animate: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const statistics = [
    { number: '5', text: 'visitas diarias por delegación' },
    { number: '30', text: 'visitas semanales aproximadamente' },
    { number: '35', text: 'días de lista de espera' },
    { number: '95%', text: 'de satisfacción total del cliente' }
  ];

  return (
    <>
      <Helmet>
        <title>Visítanos | Innotec Food Equipment</title>
        <meta name="description" content="Solicita una visita a nuestras instalaciones. Equipo de arquitectos, ingeniería y tecnología alimentaria a tu disposición." />
      </Helmet>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <h1 className="text-4xl font-bold mb-6 text-white text-center sm:text-left">Visítanos</h1>
          <div className="h-1 w-24 bg-[#FCAA0D] mx-auto sm:mx-0"></div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="prose prose-lg prose-invert max-w-none"
        >
          {/* Sección principal de Visítanos */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7 }}
              className="rounded-2xl overflow-hidden relative h-80 mb-12"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-[#000000]/80 to-transparent z-10"></div>
              <div className="absolute inset-0 flex flex-col justify-center p-8 z-20">
                <h2 className="text-3xl font-bold text-white mb-4">A tu disposición</h2>
                <p className="text-xl text-white max-w-xl">
                  DISPONEMOS DE ARQUITECTOS PROYECTOS, INGENIERÍA, EQUIPO DE TECNOLOGÍA ALIMENTARIA
                </p>
              </div>
              <img
                src="https://images.unsplash.com/photo-1581092921461-fd0e43578ba5?ixlib=rb-4.0.3&auto=format&fit=crop&w=1740&q=80"
                alt="Equipo de ingeniería y arquitectura"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mb-8 text-lg"
            >
              Un equipo multidisciplinar se pondrá en contacto para recabar todas sus necesidades, estableciendo un anteproyecto personalizado, asesorándoles y estableciendo un proyecto de inversión ajustado a su presupuesto adecuado a una necesidad de producción, en previsión, a medio plazo, tres próximos años, adecuando la línea de producción, más recomendable para sus productos.
            </motion.p>

            {/* Estadísticas */}
            <motion.div
              variants={staggerContainer}
              initial="initial"
              animate="animate"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 my-12"
            >
              {statistics.map((stat, index) => (
                <motion.div
                  key={index}
                  variants={fadeIn}
                  className="bg-gray-800/50 border border-gray-700 rounded-lg p-6 text-center hover:border-[#FCAA0D] transition-colors duration-300"
                >
                  <span className="block text-4xl font-bold text-[#FCAA0D] mb-2">{stat.number}</span>
                  <span className="text-gray-300">{stat.text}</span>
                </motion.div>
              ))}
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="mb-8 text-center text-lg bg-gray-800 p-6 rounded-lg border border-gray-700"
            >
              Recibimos una media de cinco visitas diarias por delegación hasta un máximo de 30 visitas semanales aproximadamente. Tenemos una lista de espera de unos 35 días. <span className="text-[#FCAA0D] font-semibold">En el caso que sea urgente, le daremos máxima prioridad.</span>
            </motion.p>
          </section>

          {/* Sección de Solicitud de Visita */}
          <section className="mb-12">
            <motion.h2
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-2xl font-bold mb-6 text-[#FCAA0D] text-center"
            >
              Solicitar Visita
            </motion.h2>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="bg-gray-800 rounded-lg p-8 border border-gray-700"
            >
              {formSubmitted ? (
                <div className="text-center py-12">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-green-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <h3 className="text-2xl font-bold text-white mb-2">¡Solicitud recibida!</h3>
                  <p className="text-gray-300 mb-6">Nos pondremos en contacto contigo lo antes posible para confirmar tu visita.</p>
                  <p className="text-[#FCAA0D] font-semibold">Nuestro equipo está deseando conocerte y mostrarte nuestras instalaciones.</p>
                </div>
              ) : (
                <div className="max-w-2xl mx-auto">
                  <div className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                      {[1, 2].map(step => (
                        <div key={step} className="flex items-center">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= step ? 'bg-[#FCAA0D] text-black' : 'bg-gray-700 text-gray-300'}`}>
                            {step}
                          </div>
                          {step < 2 && (
                            <div className={`h-1 w-full mx-2 ${currentStep > step ? 'bg-[#FCAA0D]' : 'bg-gray-700'}`} style={{width: '120px'}}></div>
                          )}
                        </div>
                      ))}
                    </div>
                    <p className="text-center text-gray-400 text-sm mb-8">
                      {currentStep === 1 ? 'Información de contacto' : 'Detalles de la visita'}
                    </p>
                  </div>

                  <form onSubmit={handleSubmit}>
                    {currentStep === 1 && (
                      <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label htmlFor="nombre" className="block text-white mb-2">Nombre completo*</label>
                            <input
                              type="text"
                              id="nombre"
                              name="nombre"
                              value={formData.nombre}
                              onChange={handleInputChange}
                              required
                              className="w-full px-4 py-3 rounded-lg bg-gray-700 border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-[#FCAA0D] focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label htmlFor="empresa" className="block text-white mb-2">Empresa*</label>
                            <input
                              type="text"
                              id="empresa"
                              name="empresa"
                              value={formData.empresa}
                              onChange={handleInputChange}
                              required
                              className="w-full px-4 py-3 rounded-lg bg-gray-700 border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-[#FCAA0D] focus:border-transparent"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label htmlFor="email" className="block text-white mb-2">Correo electrónico*</label>
                            <input
                              type="email"
                              id="email"
                              name="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              required
                              className="w-full px-4 py-3 rounded-lg bg-gray-700 border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-[#FCAA0D] focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label htmlFor="telefono" className="block text-white mb-2">Teléfono de contacto*</label>
                            <input
                              type="tel"
                              id="telefono"
                              name="telefono"
                              value={formData.telefono}
                              onChange={handleInputChange}
                              required
                              className="w-full px-4 py-3 rounded-lg bg-gray-700 border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-[#FCAA0D] focus:border-transparent"
                            />
                          </div>
                        </div>

                        <div className="flex justify-end mt-6">
                          <button
                            type="button"
                            onClick={nextStep}
                            className="py-3 px-6 rounded-lg font-medium text-white bg-[#FCAA0D] hover:bg-[#E69C00] transition-colors duration-300 flex items-center"
                          >
                            Siguiente
                            <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                            </svg>
                          </button>
                        </div>
                      </div>
                    )}

                    {currentStep === 2 && (
                      <div className="space-y-6">
                        <div>
                          <label htmlFor="fecha" className="block text-white mb-2">Fecha preferida de visita*</label>
                          <select
                            id="fecha"
                            name="fecha"
                            value={formData.fecha}
                            onChange={handleInputChange}
                            required
                            className="w-full px-4 py-3 rounded-lg bg-gray-700 border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-[#FCAA0D] focus:border-transparent"
                          >
                            <option value="">Selecciona una fecha</option>
                            {availableDates.map(date => (
                              <option key={date.value} value={date.value}>
                                {date.label}
                              </option>
                            ))}
                          </select>
                          <p className="text-sm text-gray-400 mt-2">
                            Próxima disponibilidad en aproximadamente 35 días
                          </p>
                        </div>

                        <div>
                          <label htmlFor="motivo" className="block text-white mb-2">Motivo de la visita*</label>
                          <textarea
                            id="motivo"
                            name="motivo"
                            value={formData.motivo}
                            onChange={handleInputChange}
                            required
                            rows={4}
                            className="w-full px-4 py-3 rounded-lg bg-gray-700 border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-[#FCAA0D] focus:border-transparent"
                            placeholder="Describe brevemente el motivo de tu visita y tus necesidades..."
                          />
                        </div>

                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="urgente"
                            name="urgente"
                            checked={formData.urgente}
                            onChange={handleInputChange}
                            className="h-5 w-5 rounded border-gray-600 text-[#FCAA0D] focus:ring-[#FCAA0D]"
                          />
                          <label htmlFor="urgente" className="ml-3 text-white">
                            Mi visita es urgente y necesito prioridad
                          </label>
                        </div>

                        <div className="flex justify-between mt-6">
                          <button
                            type="button"
                            onClick={prevStep}
                            className="py-3 px-6 rounded-lg font-medium text-white bg-gray-700 hover:bg-gray-600 transition-colors duration-300 flex items-center"
                          >
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                            </svg>
                            Atrás
                          </button>

                          <button
                            type="submit"
                            disabled={sending}
                            className={`py-3 px-6 rounded-lg font-medium text-white bg-[#FCAA0D] hover:bg-[#E69C00] transition-colors duration-300 ${sending ? 'opacity-70 cursor-not-allowed' : ''}`}
                          >
                            {sending ? (
                              <span className="flex items-center">
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Enviando...
                              </span>
                            ) : "Solicitar Visita"}
                          </button>
                        </div>
                      </div>
                    )}
                  </form>
                </div>
              )}
            </motion.div>
          </section>

          {/* Sección de Testimonios */}
          <section className="mb-4">
            <motion.h2
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-2xl font-bold mb-8 text-[#FCAA0D]"
            >
              Lo que dicen nuestros clientes
            </motion.h2>

            <motion.div
              variants={staggerContainer}
              initial="initial"
              animate="animate"
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
            >
              <motion.div
                variants={fadeIn}
                className="bg-gray-800/50 border border-gray-700 rounded-lg p-6"
              >
                <div className="flex items-center mb-4">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-r from-orange-500 to-yellow-500 flex items-center justify-center text-white font-bold">
                    A
                  </div>
                  <div className="ml-4">
                    <h3 className="text-white font-semibold">Antonio García</h3>
                    <p className="text-gray-400 text-sm">Director de Operaciones, FoodTech S.L.</p>
                  </div>
                </div>
                <p className="text-gray-300">
                  "El equipo de Innotec nos ayudó a optimizar toda nuestra línea de producción. La visita a sus instalaciones fue crucial para entender las posibilidades que teníamos. Proyecto entregado a tiempo y funcionando perfectamente."
                </p>
                <div className="flex mt-3">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg key={star} className="w-5 h-5 text-[#FCAA0D]" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </motion.div>

              <motion.div
                variants={fadeIn}
                className="bg-gray-800/50 border border-gray-700 rounded-lg p-6"
              >
                <div className="flex items-center mb-4">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">
                    M
                  </div>
                  <div className="ml-4">
                    <h3 className="text-white font-semibold">María Rodríguez</h3>
                    <p className="text-gray-400 text-sm">CEO, Conservas del Cantábrico</p>
                  </div>
                </div>
                <p className="text-gray-300">
                  "Necesitábamos una solución urgente y el equipo de Innotec nos dio prioridad. La visita y posterior proyecto superó todas nuestras expectativas. Su equipo multidisciplinar aportó ideas que no habíamos considerado."
                </p>
                <div className="flex mt-3">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg key={star} className="w-5 h-5 text-[#FCAA0D]" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          </section>
        </motion.div>
      </div>
    </>
  );
}
